import { InputRadio } from '@creditornot/cb-radio';
import React from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import FormSection from './FormSection';
import { Body3 } from '../../../../../common/typography/Typography';
import validateYupSchema from '../../../../../common/utils/validateYupSchema';
import { MenuPhotoShootOption } from '../../../SelfServeFormView/SelfServeSignUpForm/types';
import { SelfServeStepsIdentifiers } from '../../constants';
import { MenuPhotoShootOptionPreferences, MenuPhotoShootOptionPreferencesSchema } from '../validation';

type MenuPhotoShootOptionFormProps = {
  menuPhotoShootPricingConfig: Partial<{ [key in MenuPhotoShootOption]: string }>;
  photoShootOptionChoice: MenuPhotoShootOption | undefined;
  handleSubmit: (values: MenuPhotoShootOptionPreferences) => Promise<void | { [key: string]: string | Error }>;
  setNextButtonDisabled: (disabled: boolean) => void;
};

type PhotoShootOptionProps = {
  value: MenuPhotoShootOption;
  title: string;
  price: string;
  description: string;
};

const FormSectionWrapper = styled.div`
  margin-block-end: 16px;
`;

const RadioInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const RadioInputSectionWrapper = styled.div`
  flex-grow: 1;
`;

const RadioInputHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PhotoShootOption = ({ title, price, description, value }: PhotoShootOptionProps) => (
  <FormSectionWrapper>
    <FormSection>
      <Field<string | undefined> name="menuPhotoShootChoice" type="radio" value={value}>
        {({ input }) => (
          <RadioInputWrapper
            onClick={() => {
              input.onChange(value);
            }}
          >
            <>
              <InputRadio
                {...input}
                name="menuPhotoShootChoice"
                style={{
                  width: '24px',
                  height: '24px',
                  marginRight: '12px',
                }}
              />
              <RadioInputSectionWrapper>
                <RadioInputHeaderWrapper>
                  <Body3 fontWeight="semiBold">{title}</Body3>
                  <Body3>{price === '0' ? 'Free' : `${price} €`}</Body3>
                </RadioInputHeaderWrapper>
                <Body3>{description}</Body3>
              </RadioInputSectionWrapper>
            </>
          </RadioInputWrapper>
        )}
      </Field>
    </FormSection>
  </FormSectionWrapper>
);

const MenuPhotoShootOptionForm = ({
  menuPhotoShootPricingConfig,
  photoShootOptionChoice,
  handleSubmit,
  setNextButtonDisabled,
}: MenuPhotoShootOptionFormProps) => {
  return (
    <Form<MenuPhotoShootOptionPreferences>
      initialValues={{
        menuPhotoShootChoice: photoShootOptionChoice,
      }}
      onSubmit={handleSubmit}
      validate={validateYupSchema(MenuPhotoShootOptionPreferencesSchema)}
    >
      {({ handleSubmit, hasValidationErrors }) => {
        setNextButtonDisabled(hasValidationErrors);
        return (
          <form id={SelfServeStepsIdentifiers.MenuPhotoShoot} onSubmit={handleSubmit}>
            {'MERCHANT_OWN_PHOTO' in menuPhotoShootPricingConfig && (
              <PhotoShootOption
                title="Upload your own images"
                value="MERCHANT_OWN_PHOTO"
                description="We’ll collect and review your images later."
                price={menuPhotoShootPricingConfig.MERCHANT_OWN_PHOTO!}
              />
            )}
            {'WOLT_PHOTOSHOOT' in menuPhotoShootPricingConfig && (
              <PhotoShootOption
                title="Schedule a photoshoot"
                value="WOLT_PHOTOSHOOT"
                description="We’ll set up a time so a photographer can take amazing photos of your products."
                price={menuPhotoShootPricingConfig.WOLT_PHOTOSHOOT!}
              />
            )}
          </form>
        );
      }}
    </Form>
  );
};

export default MenuPhotoShootOptionForm;
